import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'redux-scaffolding-ts';
import { UserStore, UserDto } from 'stores/users/users-store';
import { useCallback, useEffect, useState } from 'react';
import TailwindMultiSelect from 'widgets/editors/tailwind-multiselect';
import { nameof } from 'utils/object';
import { StudentDto } from 'stores/students/student-model';

interface TailwindMultipleUserEditorProps {
  value: StudentDto[];
  role: string;
  locationIds: string[];
  clearable?: boolean;
  onChange?: (value: UserDto[]) => void;
  users?: UserStore;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  loadDataOnOpen?: boolean;
  onBlur?: (event: any) => void;
  onFocus?: (event: any) => void;
}

const TailwindMultipleUserEditor: React.FC<TailwindMultipleUserEditorProps> = ({
  value,
  role,
  clearable = true,
  onChange,
  users,
  locationIds,
  placeholder,
  className,
  disabled = false,
  loadDataOnOpen = false,
  onBlur,
  onFocus
}) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<UserDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const buildODataFilter = useCallback(() => {
    const oDataFilters = [];
    oDataFilters.push({ id: nameof<UserDto>('enabled'), filter: { enabled: true } });
    if (value && value.length > 0)
      oDataFilters.push({
        id: nameof<UserDto>('id'),
        filter: `not(${nameof<UserDto>('id')} in (${value.map(x => x.id).join(',')}))`
      });
    return oDataFilters;
  }, [value]);

  const getUsersMethod = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await users.getAllAsync({
        searchQuery: ``,
        orderBy: [],
        filter: buildODataFilter().map(x => x.filter),
        skip: 0,
        take: 100000,
        parameters: { IsActive: 'true' }
      });

      setOptions(result.items || []);
    } catch (error) {
      console.error('Error loading locations:', error);
      setOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, [users, buildODataFilter]);

  const onOpenDropDown = useCallback(() => {
    if (loadDataOnOpen && options.length === 0) {
      getUsersMethod();
    }
  }, [loadDataOnOpen, options.length, getUsersMethod]);

  useEffect(() => {
    if (!loadDataOnOpen) {
      getUsersMethod();
    }
  }, [loadDataOnOpen, getUsersMethod]);

  return (
    <TailwindMultiSelect
      options={
        isLoading
          ? [{ id: 'loading', text: t('Loading...'), disabled: true }]
          : options.length > 0
          ? options.map(employee => ({
              id: employee.id,
              text: employee.firstName + ' ' + employee.lastName
            }))
          : [{ id: 'no-options', text: t('No options available'), disabled: true }]
      }
      value={value?.map(employee => ({
        id: employee.id,
        text: employee.firstName + ' ' + employee.lastName
      }))}
      onChange={newValue => {
        const selectedEmployees = newValue.map(v => options.find(opt => opt.id === v.id)).filter(Boolean);
        onChange(selectedEmployees);
      }}
      placeholder={placeholder || t('Select employees')}
      clearable={clearable}
      disabled={disabled}
      className={className}
      onBlur={onBlur}
      onFocus={onFocus}
      searchable={!isLoading}
      onOpenDropDown={onOpenDropDown}
    />
  );
};

// Wire up the React component to the Redux store
export default connect(['users', UserStore])(TailwindMultipleUserEditor);
